import React, { useState, useEffect, useRef } from 'react';
import { sanitizeInput } from '../utils/sanitize';
import { validateArrayResponse, validateResponse } from '../utils/apiResponseValidator';
import { invoices as invoicesApi, customers as customersApi } from '../services/api';
import { todayLocalISO, plusDaysLocalISO, fmtDate } from '../utils/localDate';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filters, setFilters] = useState({ status: '' });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Time entry selection
  const [showTimeEntries, setShowTimeEntries] = useState(false);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [customerEntries, setCustomerEntries] = useState([]);
  const [customLineItems, setCustomLineItems] = useState([]);
  const [showCustomLineItems, setShowCustomLineItems] = useState(false);
  const selectedCustomItemIds = useRef([]);
  const [showCustomItemPicker, setShowCustomItemPicker] = useState(false);
  const [pickerCustomItems, setPickerCustomItems] = useState([]);
  const [pickerSelectedIds, setPickerSelectedIds] = useState([]);
  // Line item categories
  const LINE_ITEM_CATEGORIES = [
    { value: 'labor', label: 'Labor' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'custom', label: 'Custom Service' },
    { value: 'other', label: 'Other' }
  ];

  // New invoice form state
  const [newInvoice, setNewInvoice] = useState({
    customerId: '',
    date: todayLocalISO(),
    dueDate: plusDaysLocalISO(30),
    lineItems: [{ description: '', hours: 0, rate: 0, date: todayLocalISO(), category: 'labor' }],
    taxRate: 8.25,
    discountType: 'percent', // 'percent' or 'fixed'
    discountValue: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invRes, custRes] = await Promise.all([
        invoicesApi.list(filters.status ? { status: filters.status } : {}),
        customersApi.list({ isActive: true })
      ]);
      setInvoices(validateArrayResponse(invRes, "invoice"));
      setCustomers(validateArrayResponse(custRes, "customer"));
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (id) => {
    try {
      const response = await invoicesApi.get(id);
      setSelectedInvoice(response.data);
    } catch (error) {
      console.error('Failed to load invoice:', error);
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    loadCustomLineItems();
  };

  const handleSendInvoice = async (id) => {
    try {
      await invoicesApi.send(id);
      loadData();
      alert('Invoice marked as sent');
    } catch (error) {
      alert('Failed to send invoice');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await invoicesApi.paid(id);
      loadData();
      alert('Invoice marked as paid');
    } catch (error) {
      alert('Failed to mark paid');
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await invoicesApi.pdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  const handleEmailInvoice = async (id) => {
    try {
      const response = await invoicesApi.email(id);
      if (response.data.mailto) {
        window.location.href = response.data.mailto;
      } else {
        alert('Could not generate email. Please download the PDF and send manually.');
      }
    } catch (error) {
      console.error('Failed to email invoice:', error);
      alert('Failed to generate email link');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('invoice', file);

      const response = await fetch('/api/invoices/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();
      setImportResult(result);

      if (result.success) {
        loadData();
      }
    } catch (error) {
      console.error('Failed to import invoice:', error);
      setImportResult({ error: error.message });
    } finally {
      setImporting(false);
    }
  };

  
  // Custom line items
  const loadCustomLineItems = async () => {
    try {
      const response = await fetch('/api/line-items');
      const data = await response.json();
      setCustomLineItems(data.lineItems || []);
    } catch (err) { console.error('Failed to load custom line items:', err); }
  };

  // Load unbilled custom items for the picker modal
  const loadPickerCustomItems = async () => {
    try {
      const customerId = newInvoice.customerId;
      const url = customerId
        ? `/api/custom-items?status=unbilled&customerId=${customerId}`
        : '/api/custom-items?status=unbilled';
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setPickerCustomItems(data.items || []);
      setPickerSelectedIds([]);
    } catch (err) { console.error('Failed to load custom items for picker:', err); }
  };

  const openCustomItemPicker = () => {
    loadPickerCustomItems();
    setShowCustomItemPicker(true);
  };

  const togglePickerCustomItem = (item) => {
    setPickerSelectedIds(prev =>
      prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
    );
  };

  const togglePickerSelectAll = () => {
    if (pickerSelectedIds.length === pickerCustomItems.length) {
      setPickerSelectedIds([]);
    } else {
      setPickerSelectedIds(pickerCustomItems.map(i => i.id));
    }
  };

  const addPickerSelectedCustomItems = () => {
    const selected = pickerCustomItems.filter(i => pickerSelectedIds.includes(i.id));
    const newItems = selected.map(item => ({
      description: item.description || item.name,
      hours: item.quantity || 0,
      rate: item.unit_price || item.unitPrice || 0,
      amount: (item.quantity || 0) * (item.unit_price || item.unitPrice || 0),
      date: todayLocalISO()
    }));
    setNewInvoice({ ...newInvoice, lineItems: [...newInvoice.lineItems, ...newItems] });
    setShowCustomItemPicker(false);
    setPickerSelectedIds([]);
  };

  // Load time entries for customer
  const loadCustomerEntries = async (customerId) => {
    if (!customerId) { setCustomerEntries([]); return; }
    try {
      const response = await fetch(`/api/entries?customerId=${customerId}&status=unbilled`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setCustomerEntries(data.entries || []);
    } catch (err) { console.error("Failed to load entries:", err); }
  };

  const handleCustomerChange = (customerId) => {
    setNewInvoice({ ...newInvoice, customerId, lineItems: [{ description: '', hours: 0, rate: 0, date: todayLocalISO() }] });
    setSelectedEntryIds([]);
    if (customerId) loadCustomerEntries(customerId);
  };

  const toggleEntrySelection = (entry) => {
    const exists = selectedEntryIds.find(e => e.id === entry.id);
    if (exists) setSelectedEntryIds(selectedEntryIds.filter(e => e.id !== entry.id));
    else setSelectedEntryIds([...selectedEntryIds, entry]);
  };

  const toggleCustomItemSelection = (item) => {
    const exists = selectedCustomItemIds.current.find(i => i.id === item.id);
    if (exists) selectedCustomItemIds.current = selectedCustomItemIds.current.filter(i => i.id !== item.id);
    else selectedCustomItemIds.current = [...selectedCustomItemIds.current, item];
    setShowCustomLineItems(false);
  };

  const addSelectedCustomItems = () => {
    const newItems = selectedCustomItemIds.current.map(item => ({
      description: item.description,
      hours: item.quantity || 0,
      rate: item.unit_price || 0,
      amount: (item.quantity || 0) * (item.unit_price || 0),
      date: todayLocalISO()  // Default to today for custom items
    }));
    setNewInvoice({ ...newInvoice, lineItems: [...newInvoice.lineItems, ...newItems] });
    selectedCustomItemIds.current = [];
  };

  const addSelectedEntries = () => {
    const newItems = selectedEntryIds.map(entry => ({
      entryId: entry.id,
      description: entry.description,
      hours: (entry.hours || 0) + (entry.minutes || 0) / 60,
      rate: entry.laborRate || 0,
      amount: entry.totalCost || 0,
      date: entry.date || todayLocalISO()  // Use time entry date
    }));
    setNewInvoice({ ...newInvoice, lineItems: [...newInvoice.lineItems, ...newItems] });
    setShowTimeEntries(false);
    setSelectedEntryIds([]);
  };

  const handleCreateInvoice = async () => {
    try {
      const lineItems = newInvoice.lineItems
        .filter(item => item.description)
        .map(item => ({
          description: item.description,
          hours: parseFloat(item.hours) || 0,
          rate: parseFloat(item.rate) || 0,
          amount: (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0),
          date: item.date || todayLocalISO(),
          category: item.category || 'labor'
        }));

      // Calculate subtotals - hardware excluded from discounts
      const hardwareSubtotal = lineItems
        .filter(item => item.category === 'hardware')
        .reduce((sum, item) => sum + item.amount, 0);
      
      const discountableSubtotal = lineItems
        .filter(item => item.category !== 'hardware')
        .reduce((sum, item) => sum + item.amount, 0);

      // Calculate discount (only applies to non-hardware items)
      let discount = 0;
      const discountValue = parseFloat(newInvoice.discountValue) || 0;
      if (discountValue > 0) {
        if (newInvoice.discountType === 'percent') {
          discount = discountableSubtotal * (discountValue / 100);
        } else {
          discount = Math.min(discountValue, discountableSubtotal); // Can't discount more than discountable amount
        }
      }

      const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
      const discountedSubtotal = subtotal - discount;
      const taxAmount = discountedSubtotal * (parseFloat(newInvoice.taxRate) || 0) / 100;
      const total = discountedSubtotal + taxAmount;

      await invoicesApi.create({
        customerId: parseInt(newInvoice.customerId),
        date: newInvoice.date,
        dueDate: newInvoice.dueDate,
        lineItems,
        laborSubtotal: discountableSubtotal,
        materialsSubtotal: hardwareSubtotal,
        subtotal: discountedSubtotal,
        discountType: newInvoice.discountType,
        discountValue: discountValue,
        discount: Math.round(discount * 100) / 100,
        taxRate: parseFloat(newInvoice.taxRate) || 0,
        taxAmount,
        total,
        notes: newInvoice.notes,
        status: 'draft'
      });

      setShowCreateModal(false);
      setNewInvoice({
        customerId: '',
        date: todayLocalISO(),
        dueDate: plusDaysLocalISO(30),
        lineItems: [{ description: '', hours: 0, rate: 0, date: todayLocalISO(), category: 'labor' }],
        taxRate: 8.25,
        discountType: 'percent',
        discountValue: 0,
        notes: ''
      });
      loadData();
      alert('Invoice created!');
    } catch (error) {
      console.error('Failed to create invoice:', error);
      alert('Failed to create invoice');
    }
  };

  const addLineItem = () => {
    setNewInvoice({
      ...newInvoice,
      lineItems: [...newInvoice.lineItems, { description: '', hours: 0, rate: 0, date: todayLocalISO(), category: 'labor' }]
    });
  };

  const removeLineItem = (index) => {
    setNewInvoice({
      ...newInvoice,
      lineItems: newInvoice.lineItems.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...newInvoice.lineItems];
    items[index][field] = value;
    setNewInvoice({ ...newInvoice, lineItems: items });
  };

  const calculateTotal = () => {
    // Calculate hardware vs non-hardware subtotals
    const hardwareSubtotal = newInvoice.lineItems
      .filter(item => item.category === 'hardware')
      .reduce((sum, item) => sum + (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0), 0);
    
    const discountableSubtotal = newInvoice.lineItems
      .filter(item => item.category !== 'hardware')
      .reduce((sum, item) => sum + (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0), 0);
    
    const subtotal = hardwareSubtotal + discountableSubtotal;
    
    // Calculate discount (only applies to non-hardware items)
    let discount = 0;
    const discountValue = parseFloat(newInvoice.discountValue) || 0;
    if (discountValue > 0) {
      if (newInvoice.discountType === 'percent') {
        discount = discountableSubtotal * (discountValue / 100);
      } else {
        discount = Math.min(discountValue, discountableSubtotal);
      }
    }
    
    const discountedSubtotal = subtotal - discount;
    const tax = discountedSubtotal * (parseFloat(newInvoice.taxRate) || 0) / 100;
    const total = discountedSubtotal + tax;
    
    return { 
      subtotal, 
      hardwareSubtotal, 
      discountableSubtotal, 
      discount, 
      discountedSubtotal,
      tax, 
      total 
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (date) => fmtDate(date);

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge-warning',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const totals = calculateTotal();

  return (
    <div>
      <div className="page-header">
        <h1>Invoices</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
            📥 Import PDF
          </button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            ➕ Create Invoice
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center' }}>No invoices found</td>
                  </tr>
                ) : (
                  invoices.map(inv => (
                    <tr key={inv.id}>
                      <td>{inv.invoiceNumber}</td>
                      <td>{inv.customerName}</td>
                      <td>{formatDate(inv.date)}</td>
                      <td>{formatDate(inv.dueDate)}</td>
                      <td>{formatCurrency(inv.total)}</td>
                      <td>{formatCurrency(inv.paidAmount)}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(inv.status)}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleViewInvoice(inv.id)}>
                          View
                        </button>
                        {inv.status === 'draft' && (
                          <button className="btn btn-primary btn-sm" onClick={() => handleSendInvoice(inv.id)}>
                            Send
                          </button>
                        )}
                        {inv.status === 'sent' && (
                          <button className="btn btn-success btn-sm" onClick={() => handleMarkPaid(inv.id)}>
                            Mark Paid
                          </button>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => handleDownloadPDF(inv.id)}>
                          PDF
                        </button>
                        <button className="btn btn-info btn-sm" onClick={() => handleEmailInvoice(inv.id)}>
                          ✉️ Email
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Import Result Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Import Result</h2>
              <button className="modal-close" onClick={() => setShowImportModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {importing ? (
                <p>Importing...</p>
              ) : importResult?.success ? (
                <div>
                  <p>✅ Invoice imported successfully!</p>
                  <p>Invoice #: {importResult.invoiceNumber}</p>
                  <p>Line items: {importResult.lineItemsCount}</p>
                </div>
              ) : (
                <p>❌ Import failed: {importResult?.error}</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create Invoice</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <select
                    className="form-select"
                    value={newInvoice.customerId}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                  >
                    <option value="">Select Customer</option>
                    {customers.map(cust => (
                      <option key={cust.id} value={cust.id}>{cust.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={newInvoice.date}
                    onChange={(e) => setNewInvoice({ ...newInvoice, date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={newInvoice.dueDate}
                    onChange={(e) => setNewInvoice({ ...newInvoice, dueDate: e.target.value })}
                  />
                </div>
              </div>

              <h4 style={{ marginTop: 20, marginBottom: 10 }}>Line Items</h4>
              {newInvoice.lineItems.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '0 0 80px' }}>
                    <select
                      className="form-select"
                      value={item.category || 'labor'}
                      onChange={(e) => updateLineItem(idx, 'category', e.target.value)}
                      title="Category"
                    >
                      {LINE_ITEM_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '0 0 100px' }}>
                    <input
                      type="date"
                      className="form-input"
                      title="Work date"
                      value={item.date || ''}
                      onChange={(e) => updateLineItem(idx, 'date', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 3 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Hours"
                      value={item.hours}
                      onChange={(e) => updateLineItem(idx, 'hours', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Rate"
                      value={item.rate}
                      onChange={(e) => updateLineItem(idx, 'rate', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1, textAlign: 'right', padding: '10px 0' }}>
                    {formatCurrency((parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0))}
                  </div>
                  {newInvoice.lineItems.length > 1 && (
                    <button className="btn btn-danger btn-sm" onClick={() => removeLineItem(idx)}>×</button>
                  )}
                </div>
              ))}
              <button className="btn btn-secondary" onClick={addLineItem} style={{ marginTop: 10 }}>
                + Add Line Item
              </button>
              <button className="btn btn-secondary" onClick={() => setShowTimeEntries(true)} style={{ marginTop: 10, marginLeft: 10 }} >
                + Add Time Entries
              </button>

              <button className="btn btn-secondary" onClick={openCustomItemPicker} style={{ marginTop: 10, marginLeft: 10 }} >
                + Add Custom Items
              </button>
              

              {showTimeEntries && (
                <div style={{ marginTop: 20, padding: 15, background: '#1a1a2e', borderRadius: 8 }}>
                  <h4>Select Time Entries for {customers.find(c => c.id == newInvoice.customerId)?.name}</h4>
                  {customerEntries.length === 0 ? (
                    <p>No unbilled time entries found for this customer.</p>
                  ) : (
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {customerEntries.map(entry => (
                        <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderBottom: '1px solid #333' }}>
                          <input
                            type="checkbox"
                            checked={selectedEntryIds.some(e => e.id === entry.id)}
                            onChange={() => toggleEntrySelection(entry)}
                          />
                          <span style={{ width: 80 }}>{fmtDate(entry.date)}</span>
                          <span style={{ flex: 1 }}>{(entry.description || '').substring(0, 30)}...</span>
                          <span>{entry.hours}h {entry.minutes || 0}m</span>
                          <span>${entry.totalCost}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                    <button className="btn btn-primary" onClick={addSelectedEntries} disabled={selectedEntryIds.length === 0}>
                      Add Selected ({selectedEntryIds.length})
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowTimeEntries(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {showCustomLineItems && (
                <div style={{ marginTop: 20, padding: 15, background: '#1a1a2e', borderRadius: 8 }}>
                  <h4>Select Custom Line Items</h4>
                  {customLineItems.length === 0 ? (
                    <p>No custom line items. Create them in Accounting → Line Items.</p>
                  ) : (
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {customLineItems.map(item => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderBottom: '1px solid #333', cursor: 'pointer', background: '#252545', marginBottom: 5, borderRadius: 4 }} onClick={() => {
                          setNewInvoice({ ...newInvoice, lineItems: [...newInvoice.lineItems, { description: item.description, hours: item.quantity || 0, rate: item.unit_price || 0, amount: (item.quantity || 0) * (item.unit_price || 0), date: todayLocalISO() }] });
                          setShowCustomLineItems(false);
                        }}>
                          <span style={{ flex: 1 }}>{item.description}</span>
                          <span>Qty: {item.quantity}</span>
                          <span>Price: ${item.unit_price}</span>
                          <strong>${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <button className="btn btn-secondary" onClick={() => setShowCustomLineItems(false)}>Close</button>
                  </div>
                </div>
              )}

<div className="form-row" style={{ marginTop: 20 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Tax Rate (%)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={newInvoice.taxRate}
                    onChange={(e) => setNewInvoice({ ...newInvoice, taxRate: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Discount Type</label>
                  <select
                    className="form-select"
                    value={newInvoice.discountType}
                    onChange={(e) => setNewInvoice({ ...newInvoice, discountType: e.target.value })}
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Discount Value</label>
                  <input
                    type="number"
                    className="form-input"
                    value={newInvoice.discountValue}
                    onChange={(e) => setNewInvoice({ ...newInvoice, discountValue: e.target.value })}
                    placeholder={newInvoice.discountType === 'percent' ? '10' : '50'}
                  />
                </div>
              </div>
              
              {newInvoice.discountValue > 0 && (
                <div style={{ marginTop: 10, padding: 10, background: '#1a3a1a', borderRadius: 4, fontSize: '0.9em' }}>
                  <strong>Discount applied to labor/custom items only:</strong><br/>
                  Hardware subtotal: {formatCurrency(totals.hardwareSubtotal)} (not discounted)<br/>
                  Discountable subtotal: {formatCurrency(totals.discountableSubtotal)}<br/>
                  Discount: -{formatCurrency(totals.discount)}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={newInvoice.notes}
                    onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ marginTop: 20, textAlign: 'right' }}>
                <p>Hardware Subtotal: {formatCurrency(totals.hardwareSubtotal)}</p>
                <p>Labor/Custom Subtotal: {formatCurrency(totals.discountableSubtotal)}</p>
                {totals.discount > 0 && (
                  <p style={{ color: '#4ade80' }}>Discount: -{formatCurrency(totals.discount)}</p>
                )}
                <p>Subtotal (after discount): {formatCurrency(totals.discountedSubtotal)}</p>
                <p>Tax ({newInvoice.taxRate}%): {formatCurrency(totals.tax)}</p>
                <p><strong>Total: {formatCurrency(totals.total)}</strong></p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateInvoice}>Create Invoice</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Picker Modal */}
      {showCustomItemPicker && (
        <div className="modal-overlay" onClick={() => setShowCustomItemPicker(false)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select Custom Items</h2>
              <button className="modal-close" onClick={() => setShowCustomItemPicker(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {pickerCustomItems.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>No unbilled custom items available</p>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #374151', borderRadius: 6 }}>
                  {pickerCustomItems.map(item => (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      borderBottom: '1px solid #374151',
                      background: pickerSelectedIds.includes(item.id) ? '#1e3a5f' : 'transparent',
                      cursor: 'pointer'
                    }} onClick={() => togglePickerCustomItem(item)}>
                      <input type="checkbox" checked={pickerSelectedIds.includes(item.id)} readOnly style={{ margin: 0, width: 18, height: 18 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{item.name}</div>
                        {item.description && <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.description}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13 }}>{item.quantity} × {formatCurrency(item.unitPrice)}</div>
                        <div style={{ fontSize: 13, color: '#34d399', fontWeight: 500 }}>{formatCurrency(item.totalPrice)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn btn-secondary" onClick={togglePickerSelectAll}>
                {pickerSelectedIds.length === pickerCustomItems.length ? 'Deselect All' : 'Select All'}
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" onClick={() => setShowCustomItemPicker(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={addPickerSelectedCustomItems} disabled={pickerSelectedIds.length === 0}>
                  Add Selected ({pickerSelectedIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {selectedInvoice && (
        <div className="modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Invoice {selectedInvoice.invoiceNumber}</h2>
              <button className="modal-close" onClick={() => setSelectedInvoice(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div>
                  <strong>Customer:</strong> {selectedInvoice.customer?.name}
                </div>
                <div>
                  <strong>Date:</strong> {formatDate(selectedInvoice.date)}
                </div>
                <div>
                  <strong>Due:</strong> {formatDate(selectedInvoice.dueDate)}
                </div>
              </div>
              
              <h4 style={{ marginTop: 20, marginBottom: 10 }}>Line Items</h4>
              <table className="table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Hours</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.lineItems?.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <span className={`badge ${
                          item.category === 'hardware' ? 'badge-warning' : 
                          item.category === 'custom' ? 'badge-info' : 'badge-success'
                        }`}>
                          {item.category || 'Labor'}
                        </span>
                      </td>
                      <td>{item.date ? formatDate(item.date) : '-'}</td>
                      <td>{item.description}</td>
                      <td>{item.hours}</td>
                      <td>{formatCurrency(item.rate)}</td>
                      <td>{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div style={{ marginTop: 20, textAlign: 'right' }}>
                <p>Hardware Subtotal: {formatCurrency(selectedInvoice.materialsSubtotal || 0)}</p>
                <p>Labor/Custom Subtotal: {formatCurrency(selectedInvoice.laborSubtotal || 0)}</p>
                {selectedInvoice.discount > 0 && (
                  <p style={{ color: '#4ade80' }}>
                    Discount ({selectedInvoice.discountType === 'percent' ? `${selectedInvoice.discountValue}%` : 'Fixed'}): 
                    -{formatCurrency(selectedInvoice.discount)}
                  </p>
                )}
                <p>Subtotal: {formatCurrency(selectedInvoice.subtotal)}</p>
                <p>Tax ({selectedInvoice.taxRate}%): {formatCurrency(selectedInvoice.taxAmount)}</p>
                <p><strong>Total: {formatCurrency(selectedInvoice.total)}</strong></p>
                <p>Paid: {formatCurrency(selectedInvoice.paidAmount)}</p>
              </div>
              
              {selectedInvoice.notes && (
                <div style={{ marginTop: 20 }}>
                  <strong>Notes:</strong>
                  <p>{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedInvoice(null)}>Close</button>
              <button className="btn btn-secondary" onClick={() => handleDownloadPDF(selectedInvoice.id)}>Download PDF</button>
              <button className="btn btn-info" onClick={() => handleEmailInvoice(selectedInvoice.id)}>✉️ Email</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
